import type { AiScribeStep } from "@elation/transcribe";

const INITIAL_SCRIBE_STEP = "initial" satisfies AiScribeStep;

export interface ResolveVisitRecordingStepInput {
  encounterId: number | undefined;
  activeEncounterId: number | undefined | null;
  step: AiScribeStep;
  isScribeStateCurrent: boolean;
  isScribeInProgress: boolean;
}

/**
 * Picks the scribe step a hook instance should display for `encounterId`.
 *
 * `step` (and, absent an explicit binding, `activeEncounterId`) come from an
 * app-level singleton scribe session — only one recording can exist at a
 * time, but this hook is mounted once per encounter-scoped surface (visit
 * route, patient chrome). `isScribeInProgress` alone is not enough to gate
 * on: it echoes the singleton's step even when the singleton is bound to a
 * *different* encounter than this instance, which would leak encounter A's
 * live recording banner into encounter B's UI.
 *
 * We only refuse to echo when the singleton is bound to a *known, different*
 * encounter. When the binding is merely unknown (`activeEncounterId == null`,
 * e.g. a fresh remount before this instance's own start/resume call has run)
 * we keep echoing `step` — otherwise a same-encounter remount would flash
 * back to `initial` while a recording is genuinely still in progress.
 * `isScribeStateCurrent` already covers the note-generation rebind case
 * (finish requested for this exact encounter), so it takes precedence over
 * the mismatch check.
 */
export function resolveVisitRecordingStep({
  encounterId,
  activeEncounterId,
  step,
  isScribeStateCurrent,
  isScribeInProgress,
}: ResolveVisitRecordingStepInput): AiScribeStep {
  const isBoundToDifferentKnownEncounter =
    activeEncounterId != null &&
    encounterId != null &&
    activeEncounterId !== encounterId;

  if (isBoundToDifferentKnownEncounter && !isScribeStateCurrent) {
    return INITIAL_SCRIBE_STEP;
  }

  return isScribeStateCurrent || isScribeInProgress
    ? step
    : INITIAL_SCRIBE_STEP;
}

export interface VisitRecordingV2 {
  step: AiScribeStep;
  isReady: boolean;
  isSupported?: boolean;
  microphoneDeviceId?: string | null;
  needsMicrophoneSelection?: boolean;
  audioContext?: AudioContext | null;
  audioSource?: MediaStreamAudioSourceNode | null;
  errorMessage?: string | null;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}
